import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

const BUCKET_NAME = 'blog-images';

/**
 * Uploads an image to the Supabase storage bucket
 * @param file The file to upload
 * @param progressCallback Optional callback for upload progress
 * @returns The public URL of the uploaded image
 */
export async function uploadImage(
  file: File,
  progressCallback?: (progress: number) => void
): Promise<string> {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }
    
    // Generate a unique filename to avoid collisions
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `${fileName}`;
    
    // Upload the file to Supabase storage
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });
    
    if (error) {
      throw error;
    }
    
    // Get the public URL of the file
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(filePath);
    
    return publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}

/**
 * Validates if an image URL belongs to our Supabase storage
 * @param url The URL to validate
 * @returns True if the URL belongs to our Supabase storage
 */
export function isSupabaseStorageUrl(url: string): boolean {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  return url.includes(supabaseUrl) && url.includes(BUCKET_NAME);
}

/**
 * Extracts the file path from a Supabase storage URL
 * @param url The Supabase storage URL
 * @returns The file path within the bucket
 */
export function getFilePathFromUrl(url: string): string | null {
  try {
    if (!isSupabaseStorageUrl(url)) {
      return null;
    }
    
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const bucketIndex = pathParts.findIndex(part => part === BUCKET_NAME);
    
    if (bucketIndex === -1 || bucketIndex === pathParts.length - 1) {
      return null;
    }
    
    return pathParts.slice(bucketIndex + 1).join('/');
  } catch (error) {
    console.error('Error parsing URL:', error);
    return null;
  }
}

/**
 * Deletes an image from Supabase storage
 * @param url The URL of the image to delete
 * @returns True if deletion was successful
 */
export async function deleteImage(url: string): Promise<boolean> {
  try {
    const filePath = getFilePathFromUrl(url);
    
    if (!filePath) {
      throw new Error('Invalid Supabase storage URL');
    }
    
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([filePath]);
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting image:', error);
    return false;
  }
} 